import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Cashier.css";

// Import components
import Approval from "./Approval";
import MakeOrder from "./Homee";
import Report from "./Printing";

const PAYMENT_API = "https://okna.co.tz/mrms/Payment.php";

export default function Cashier() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");

  const [activeNav, setActiveNav] = useState("Dashboard");
  const [stats, setStats] = useState({
    pendingPayments: 0,
    approvedPayments: 0,
    totalPayments: 0
  });

  useEffect(() => {
    if (!token) {
      Swal.fire("Session Expired", "Please login again", "warning");
      navigate("/");
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${PAYMENT_API}?token=${token}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to fetch");

      const data = await res.json();

      // Guard against non-array response
      if (!Array.isArray(data)) {
        console.error("Payment API did not return an array:", data);
        return;
      }

      setStats({
        pendingPayments: data.filter(p => p.status === "PENDING").length,
        approvedPayments: data.filter(p => p.status === "PAID").length,
        totalPayments: data.length
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load statistics", "error");
    }
  };

  const handleLogout = async () => {
    const confirm = await Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel"
    });

    if (confirm.isConfirmed) {
      localStorage.clear();
      navigate("/");
    }
  };

  const navItems = [
    { id: "Dashboard", label: "Dashboard", icon: "📊" },
    { id: "Make Order", label: "Make Order", icon: "📝" },
    { id: "Payment Approval", label: "Payment Approval", icon: "💳" },
    { id: "Print Order", label: "Print Order", icon: "🖨️" }
  ];

  const getPageTitle = () => {
    switch (activeNav) {
      case "Dashboard": return "Dashboard Overview";
      case "Make Order": return "Create New Order";
      case "Payment Approval": return "Review & Approve Payments";
      case "Print Order": return "Print Receipts & Reports";
      default: return "Dashboard";
    }
  };

  return (
    <div className="cashier-app">
      {/* Header */}
      <header className="cashier-header">
        <div className="header-container">
          {/* Logo + Pending count only */}
          <div className="logo-section">
            <div className="logo">
              <span className="logo-icon">💰</span>
              <span className="logo-text">Cashier Portal</span>
            </div>

            <div className="header-stats">
              <div
                className="header-stat pending-stat"
                onClick={() => setActiveNav("Payment Approval")}
                style={{ cursor: "pointer" }}
                title="Click to view pending payments"
              >
                <span className="stat-icon">⏳</span>
                <span className="stat-value">{stats.pendingPayments}</span>
                <span className="stat-label">Pending</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="nav-menu">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveNav(item.id)}
                className={`nav-btn ${activeNav === item.id ? "active" : ""}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}

            {/* User Info & Logout */}
            <div className="user-section">
              <div className="user-avatar">
                {staff.full_name?.charAt(0) || "U"}
              </div>
              <div className="user-details">
                <p className="user-name">{staff.full_name || "User"}</p>
                <p className="user-role">{staff.role || "Cashier"}</p>
              </div>
              <button onClick={handleLogout} className="logout-btn" title="Logout">
                🚪
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="cashier-main">
        <div className="content-container">
          {/* Page Header */}
          <div className="page-header">
            <h1 className="page-title">{getPageTitle()}</h1>
            <p className="page-description">
              {activeNav === "Dashboard" && "Welcome back! Here's an overview of your cashier activities."}
              {activeNav === "Make Order" && "Create new orders for customers. Items will be sent to the kitchen."}
              {activeNav === "Payment Approval" && "Review and approve pending payment requests from waiters."}
              {activeNav === "Print Order" && "Generate and print receipts for completed orders."}
            </p>
          </div>

          {/* Content Area */}
          <div className="content-area">

            {/* DASHBOARD */}
            {activeNav === "Dashboard" && (
              <div className="dashboard-view">
                {/* Stats Grid - only pending, approved, total */}
                <div className="stats-grid">
                  <div
                    className="stat-card pending-card"
                    onClick={() => setActiveNav("Payment Approval")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-icon-wrapper pending-icon">⏳</div>
                    <div className="stat-info">
                      <h4 className="stat-title">Pending Approvals</h4>
                      <p className="stat-number">{stats.pendingPayments}</p>
                      <span className="stat-action">Review Now →</span>
                    </div>
                  </div>

                  <div className="stat-card approved-card">
                    <div className="stat-icon-wrapper approved-icon">✅</div>
                    <div className="stat-info">
                      <h4 className="stat-title">Approved Payments</h4>
                      <p className="stat-number">{stats.approvedPayments}</p>
                      <span className="stat-action">Completed</span>
                    </div>
                  </div>

                  <div className="stat-card total-card">
                    <div className="stat-icon-wrapper total-icon">📊</div>
                    <div className="stat-info">
                      <h4 className="stat-title">Total Transactions</h4>
                      <p className="stat-number">{stats.totalPayments}</p>
                      <span className="stat-action">All Time</span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions-section">
                  <h3 className="section-title">Quick Actions</h3>
                  <div className="actions-grid">
                    {[
                      { id: "Make Order", label: "Create Order", icon: "📝", color: "#1abc9c", desc: "Take new customer order" },
                      { id: "Payment Approval", label: "Approve Payments", icon: "💳", color: "#f39c12", desc: "Review pending payments" },
                      { id: "Print Order", label: "Print Receipts", icon: "🖨️", color: "#3498db", desc: "Generate order receipts" },
                      { id: "refresh", label: "Refresh Data", icon: "🔄", color: "#95a5a6", desc: "Update statistics" }
                    ].map((action) => (
                      <button
                        key={action.label}
                        onClick={() => {
                          if (action.id === "refresh") {
                            fetchStats();
                            Swal.fire({
                              title: "Refreshed",
                              text: "Statistics updated",
                              icon: "success",
                              timer: 1500,
                              showConfirmButton: false
                            });
                          } else {
                            setActiveNav(action.id);
                          }
                        }}
                        className="action-card"
                        style={{ borderTopColor: action.color }}
                      >
                        <div className="action-icon" style={{ backgroundColor: `${action.color}15`, color: action.color }}>
                          {action.icon}
                        </div>
                        <div className="action-content">
                          <h4 className="action-title">{action.label}</h4>
                          <p className="action-desc">{action.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="recent-activity-section">
                  <h3 className="section-title">Recent Activity</h3>
                  <div className="activity-card">
                    <div className="activity-empty">
                      <div className="empty-icon">📋</div>
                      <p className="empty-text">Ready to process orders</p>
                      <p className="empty-hint">Use the navigation menu to manage orders and payments</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MAKE ORDER */}
            {activeNav === "Make Order" && (
              <div className="component-wrapper">
                <MakeOrder />
              </div>
            )}

            {/* PAYMENT APPROVAL */}
            {activeNav === "Payment Approval" && (
              <div className="component-wrapper">
                <Approval />
              </div>
            )}

            {/* PRINT ORDER */}
            {activeNav === "Print Order" && (
              <div className="component-wrapper">
                <Report />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}